import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Platform,
  useWindowDimensions,
  ImageBackground,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Pressable
} from 'react-native';
import {
  YStack,
  XStack,
  H1,
  H2,
  H4,
  Text,
  Button,
  SizableText,
  Card,
  Separator,
  H3,
  Tabs,
  Paragraph,
  Input,
  H6
} from 'tamagui';
import { LinearGradient } from 'expo-linear-gradient';
import { useSelector, useDispatch } from 'react-redux';
import { useNetInfo } from '@react-native-community/netinfo';
import { useNavigation, useRoute } from '@react-navigation/native';
import { FontAwesome, FontAwesome6, AntDesign } from '@expo/vector-icons';
import { motion } from 'framer-motion';
import LottieView from '@utils/lottieAnimation';
import { registerAndPay } from "@api/api";
import loginUser from '@api/login';
import apis, { registerAndPay as registerUser } from '@api/api';
import createRandomGoogleUser from './LoginScreen/utils/createGoogleUser';
// Global imports
import styles from '@stylesheet';
import { DefaultRootState } from '@reduxLocal/persistState';
import {
  SHOW_AUTH_MODAL,
  HIDE_ALL_MODALS,
  SHOW_LOADING_MODAL,
  HIDE_LOADING_MODAL,
  LOGIN_FLIP,
  SUCCESS,
  SHOW_PASSWORD_RESET_MODAL
} from '@reduxLocal/actions/types';
import { useResponsiveWeb } from '@hooks/useResponsiveWeb';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import StripePaymentComponent from './Components/StripePaymentComponent';
import GoogleButton from "./Components/Authorisation/GoogleButtonLogin";
import MicrosoftButton from './Components/Authorisation/MicrosoftButton';
import LinkedInButton from './Components/Authorisation/LinkedInButton';
import DiscordButton from './Components/Authorisation/DiscordButton';
import AppleButton from './Components/Authorisation/AppleButton';
import { Formik } from 'formik';
import { handleOAuthResponse } from './Components/Authorisation/utils/authUtils';
import { blurPreventionState } from '@utils/blurPrevention';

// Type definitions
interface RegistrationRouteParams {
  productId?: string;
  code?: string;
}

interface RegistrationFormData {
  // Personal Information
  fullName: string;
  email: string;
  phone?: string;

  // Business Information
  companyName: string;
  industry: string;
  companySize: string;
  role: string;

  // Interest and Goals
  primaryInterest: string;
  businessGoals: string[];
  currentTools: string[];

  // Technical Preferences
  deploymentPreference: string;
  integrationNeeds: string[];

  // Communication Preferences
  communicationPreference: string;
  marketingConsent: boolean;
  termsAccepted: boolean;
}

interface RegistrationProps {
  flipCard: () => void;
  onNewUserSignup?: (userData: any) => void;
  code?: string | { code: string };
  productId?: string;
}

/**
 * Registration Screen Component
 * 
 * A comprehensive registration screen that captures user interest and business information
 * using the generateSchemaForm hook with modern responsive styling similar to LoginScreen and TutorialModal.
 * 
 * Features:
 * - Progressive form with logical grouping
 * - Responsive design for web and mobile
 * - Modern glassmorphism styling
 * - Integration with global stylesheet utilities
 * - Form validation and submission handling
 */
const Registration = ({ flipCard, onNewUserSignup, code, productId }: RegistrationProps) => {
  const navigation = useNavigation();
  const [activeState, setActiveState] = useState(null);

  // On web, use CSS-based responsive design (viewport units, percentages) instead of JavaScript resize listeners
  // This prevents re-renders when keyboard appears. For native, use Dimensions API.
  // CRITICAL: We must call useWindowDimensions() hook (Rules of Hooks), but we'll ignore its updates for keyboard resizes on web
  const windowDimensionsHook = useWindowDimensions();
  const { getResponsivePadding, getResponsiveFontSize, getResponsiveStyles } = useResponsiveWeb();

  // Use CSS-based breakpoints on web (no JavaScript calculations needed)
  // Breakpoints are determined by CSS media queries via className/style, not JavaScript
  // CRITICAL: Use empty deps so this never recalculates - CSS handles responsive behavior on web
  const responsiveBreakpoints = useMemo(() => {
    // On web, we'll use CSS classes and viewport units, so we only need initial values
    // These are used for initial render only - CSS handles the rest
    if (Platform.OS === 'web') {
      // Use a simple initial check, but CSS will handle actual responsive behavior
      const initialWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
      return {
        isMobile: initialWidth < 768,
        isTablet: initialWidth >= 768 && initialWidth < 1024,
        isDesktop: initialWidth >= 1024,
        isLargeDesktop: initialWidth >= 1440,
        // Height classifications - use viewport units in CSS instead
        isShortScreen: false, // CSS handles this
        isMediumScreen: false, // CSS handles this
        isTallScreen: false, // CSS handles this
        isMobilePortrait: false, // CSS handles this
        isMobileLandscape: false, // CSS handles this
        isCompactHeight: false, // CSS handles this
      };
    }

    // For native, calculate from actual dimensions (but this memo never recalculates due to empty deps)
    const { width, height } = windowDimensionsHook;
    return {
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024,
      isLargeDesktop: width >= 1440,
      isShortScreen: height < 600,
      isMediumScreen: height >= 600 && height < 900,
      isTallScreen: height >= 900,
      isMobilePortrait: width < 768 && height > width,
      isMobileLandscape: width < 768 && width > height,
      isCompactHeight: height < 700,
    };
  }, []); // Empty deps - CSS handles responsive behavior on web, no need to recalculate

  // Use windowDimensions only for native, ignore hook updates on web (use CSS instead)
  const windowDimensions = Platform.OS === 'web'
    ? { width: typeof window !== 'undefined' ? window.innerWidth : 1024, height: typeof window !== 'undefined' ? window.innerHeight : 768 }
    : windowDimensionsHook;

  // Get responsive styles
  const responsive = useMemo(() => ({
    spacing: {
      xs: responsiveBreakpoints.isMobile ? 4 : 6,
      sm: responsiveBreakpoints.isMobile ? 8 : 12,
      md: responsiveBreakpoints.isMobile ? 12 : 16,
      lg: responsiveBreakpoints.isMobile ? 16 : 24,
      xl: responsiveBreakpoints.isMobile ? 24 : 32,
    },
    typography: {
      xs: responsiveBreakpoints.isMobile ? 10 : 12,
      sm: responsiveBreakpoints.isMobile ? 12 : 14,
      base: responsiveBreakpoints.isMobile ? 14 : 16,
      lg: responsiveBreakpoints.isMobile ? 16 : 18,
      xl: responsiveBreakpoints.isMobile ? 18 : 20,
    }
  }), [responsiveBreakpoints]);

  async function handleUserData(res) {
    let amendedData = res.data.data;

    if (Platform.OS !== 'web') {
      let activeTokens = amendedData.notification_info?.map(item => item.notification_token) || [];

      // TODO: Get notification token from appropriate service
      const notificationToken = ''; // Placeholder - should get actual token
      if (!activeTokens.length || activeTokens.includes(notificationToken)) {
        await apis.updateNotificationToken(notificationToken, amendedData.userData.id);
      }

      amendedData.expireAt = Date.now() + 1000 * 60 * 60 * 24 * 7;
    }
    return amendedData;
  }

  // Redux state and dispatch
  const dispatch = useDispatch();
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const isConnected = useNetInfo().isConnected;

  // Local state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [formData, setFormData] = useState<Partial<RegistrationFormData> | null>(null);
  const loginForm = useRef<any>(null);
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [codeVerified, setCodeVerified] = useState(null);
  const notificationToken = useSelector((state: DefaultRootState) => state.app.notificationToken);

  // Blur prevention for web touch devices
  const emailInputRef = useRef<any>(null);
  const passwordInputRef = useRef<any>(null);
  const emailFocusTimeRef = useRef<number>(0);
  const passwordFocusTimeRef = useRef<number>(0);
  const isTouchDeviceRef = useRef<boolean>(false);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
  }, []);

  const getCurrentTime = useCallback(() => {
    return Platform.OS === 'web' && typeof performance !== 'undefined' ? performance.now() : Date.now();
  }, []);

  // Blur prevention handlers for email input
  const handleEmailFocus = useCallback((e: any) => {
    if (Platform.OS === 'web' && isTouchDeviceRef.current) {
      emailFocusTimeRef.current = getCurrentTime();
      // Start blur prevention proactively on focus for web touch devices
      // This prevents validation from running during the keyboard appearance transition
      blurPreventionState.startPrevention();
    }
  }, [getCurrentTime]);

  const handleEmailBlur = useCallback((e: any) => {
    if (Platform.OS === 'web' && isTouchDeviceRef.current) {
      const now = getCurrentTime();
      const timeSinceFocus = now - emailFocusTimeRef.current;
      if (timeSinceFocus < 1000) {
        // Start blur prevention to block validation and form remounting
        blurPreventionState.startPrevention();
        // Refocus the input to restore focus that was lost due to keyboard appearance
        // Use requestAnimationFrame to refocus after the browser has finished processing the blur
        requestAnimationFrame(() => {
          // Small delay to ensure the blur event has fully processed
          setTimeout(() => {
            if (emailInputRef.current) {
              try {
                emailInputRef.current.focus();
              } catch (err) {
                console.warn('Failed to refocus email input after false blur:', err);
              }
            }
          }, 10);
        });
        // Don't process this blur event
        return;
      }
    }
    blurPreventionState.stopPrevention();
  }, [getCurrentTime]);

  // Blur prevention handlers for password input
  const handlePasswordFocus = useCallback((e: any) => {
    if (Platform.OS === 'web' && isTouchDeviceRef.current) {
      passwordFocusTimeRef.current = getCurrentTime();
      // Start blur prevention proactively on focus for web touch devices
      // This prevents validation from running during the keyboard appearance transition
      blurPreventionState.startPrevention();
    }
  }, [getCurrentTime]);

  const handlePasswordBlur = useCallback((e: any) => {
    if (Platform.OS === 'web' && isTouchDeviceRef.current) {
      const now = getCurrentTime();
      const timeSinceFocus = now - passwordFocusTimeRef.current;
      if (timeSinceFocus < 1000) {
        // Start blur prevention to block validation and form remounting
        blurPreventionState.startPrevention();
        // Refocus the input to restore focus that was lost due to keyboard appearance
        // Use requestAnimationFrame to refocus after the browser has finished processing the blur
        requestAnimationFrame(() => {
          // Small delay to ensure the blur event has fully processed
          setTimeout(() => {
            if (passwordInputRef.current) {
              try {
                passwordInputRef.current.focus();
              } catch (err) {
                console.warn('Failed to refocus password input after false blur:', err);
              }
            }
          }, 10);
        });
        // Don't process this blur event
        return;
      }
    }
    blurPreventionState.stopPrevention();
  }, [getCurrentTime]);

  // Helper function to extract code string from various formats
  const getCodeString = useCallback((codeValue: string | { code: string } | undefined): string | undefined => {
    if (!codeValue) return undefined;
    if (typeof codeValue === 'string') return codeValue;
    if (typeof codeValue === 'object' && 'code' in codeValue) return codeValue.code;
    return undefined;
  }, []);

  // Code verification useEffect
  useEffect(() => {
    const verifyCode = async () => {
      try {
        // Handle both string and object formats for code
        const codeString = getCodeString(code);
        if (!codeString) {
          setCodeVerified(null);
          return;
        }
        const res = await apis.verifyCouponCode(codeString);
        setCodeVerified(res.data);
      } catch (error) {
        console.error("Code verification failed:", error);
        setCodeVerified(null);
      }
    }

    if (code) {
      verifyCode();
    }
  }, [code, getCodeString]);

  // Refs
  const formRef = useRef<any>(null);
  const scrollViewRef = useRef<any>(null);
  const paymentRef = useRef<any>(null);

  useEffect(() => {
    setFormData(null);
  }, [])

  // REMOVED: console.log("Registration screen rendered") - component is memoized so shouldn't re-render unnecessarily

  // Memoize the registration schema to prevent GenerateSchemaForm from re-rendering unnecessarily
  const registrationSchema = useMemo(() => [{
    title: "fullName",
    type: "text",
    content: {
      label: "Full Name",
      validation: {
        label: "Full Name",
        required: true
      }
    },
  }, {
    title: "email",
    type: "email",
    content: { validation: { required: true } },
  }, {
    title: "contactNumber",
    type: "phone",
    content: {
      label: "Contact Number",
      validation: { required: true }
    },
  }, {
    title: "password",
    type: "password",
    content: {
      label: "Password",
      validation: {
        label: "Password",
        required: true,
        repeatPassword: true,
        minLength: 6,
        errorMessage: "Password must be at least 6 characters"
      }
    },
  }], []); // Empty deps - schema never changes

  // Memoize the submit function to prevent GenerateSchemaForm from re-rendering unnecessarily
  const handleRegistrationSubmit = useCallback(async (data) => {
    try {
      // Use the regular registerUser function instead of registerAndPay
      const response = await registerUser({
        ...data,
        productId,
        code,
        source: 'registration_screen',
        timestamp: new Date().toISOString()
      });
      // Set form data for success display
      setFormData(data);
      // Check if this is a new user registration
      // Since registerUser returns a function on error, check if response is not a function
      if (onNewUserSignup && typeof response !== 'function') {
        // Call the callback with user data
        onNewUserSignup({
          ...data,
          isNewUser: true,
          registrationResponse: response
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Handle error appropriately
      throw error;
    }
  }, [productId, code, onNewUserSignup]); // Only recreate if these dependencies change

  // const responsive = getResponsiveStyles();

  // Handle payment success
  const handlePaymentSuccess = useCallback((result) => {
    console.log('🎉 Payment successful:', result);
    dispatch({
      type: SHOW_AUTH_MODAL,
      data: {
        content: `Payment successful! Welcome to SmartSaaS ${result.product?.name || 'Premium'}. You can now access all features.`,
        type: "success"
      }
    });

    // Navigate to dashboard or login after successful payment
    setTimeout(() => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else if (flipCard) {
        flipCard();
      }
    }, 2000);
  }, [dispatch, navigation]);

  // Handle payment error
  const handlePaymentError = useCallback((error) => {
    console.error('💳 Payment error:', error);
    dispatch({
      type: SHOW_AUTH_MODAL,
      data: {
        content: error.message || "Payment failed. Please try again.",
        type: "error"
      }
    });
  }, [dispatch]);

  function handleResetPress() {
    dispatch({ type: SHOW_PASSWORD_RESET_MODAL })
  }

  // Handle payment cancellation
  const handlePaymentCancel = useCallback(() => {
    setFormData(null);
    // Just log, no need to show error for user cancellation
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (values: RegistrationFormData) => {
    if (!isConnected) {
      dispatch({ type: HIDE_ALL_MODALS });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "No network connection. Please check your internet connection and try again.",
          type: "error"
        }
      });
      return;
    }

    setIsSubmitting(true);
    dispatch({ type: SHOW_LOADING_MODAL });

    try {
      // Submit registration data
      const response = await registerUser({
        ...values,
        source: 'registration_screen',
        timestamp: new Date().toISOString()
      });

      // If response is a function, it means there was an error
      if (typeof response === 'function') {
        throw new Error('Registration failed');
      }

      // Success case
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "Registration successful! Welcome to SmartSaaS. Please check your email for next steps.",
          type: "success"
        }
      });

      // Navigate to login or dashboard based on response
      setTimeout(() => {
        if (navigation.canGoBack()) {
          navigation.goBack();
        } else if (flipCard) {
          flipCard();
        }
      }, 2000);
    } catch (error) {
      console.error('Registration error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: error.message || "Registration failed. Please try again.",
          type: "error"
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [isConnected, dispatch, navigation]);

  // // Generate the form using the schema
  // const RegistrationForm = generateSchemaForm({
  //   schema: registrationSchema,
  //   onSubmit: handleSubmit,
  //   submitText: "Complete Registration",
  //   hasTitle: true,
  //   columns: responsiveBreakpoints.isMobile ? 1 : 2,
  //   customStyling: {
  //     backgroundColor: 'transparent',
  //     padding: 0
  //   },
  //   scrollEnabled: true,
  //   resetOnSubmit: false,
  //   retainInitialValues: false,
  //   initialData: formData,
  //   buttonColor: '#3B82F6',
  //   textColor: theme ? '#1F2937' : '#F9FAFB'
  // });

  // Handle back navigation
  const handleBack = useCallback(() => {
    // Use a more generic navigation method
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // Fallback or close modal
      flipCard?.();
    }
  }, [navigation, flipCard]);

  // Responsive styling
  const containerStyles = useMemo(() => ({
    ...styles.modernCard,
    ...styles.loginMainCard,
    paddingHorizontal: Platform.OS === 'web' ? '5%' : windowDimensions.width * 0.05, // Use CSS percentage on web
    minHeight: 500,
    ...(responsiveBreakpoints.isMobile ? {
      // Remove height constraints for mobile to allow full stretch
      flex: 1,
      maxHeight: undefined,
      paddingBottom: 20, // Add bottom padding for mobile
    } : {
        maxHeight: Platform.OS === 'web' ? ('70vh' as any) : windowDimensions.height * 0.7, // Use CSS viewport unit on web
        maxWidth: Platform.OS === 'web' ? '90%' : Math.min(800, windowDimensions.width * 0.9), // Use CSS percentage on web
    }),
    shadowOpacity: 0,
    alignSelf: 'center' as const,
    margin: responsiveBreakpoints.isMobile ? getResponsivePadding(8) : getResponsivePadding(20), // Reduce margin for wider mobile layout
  } as any), [windowDimensions, responsiveBreakpoints, getResponsivePadding]);

  const animationStyles = useMemo(() => ({
    ...styles.loginAnimationContainer,
    maxHeight: responsiveBreakpoints.isMobile ? 120 : 160,
    minHeight: responsiveBreakpoints.isMobile ? 80 : 120,
  }), [responsiveBreakpoints]);

  return (
    <View
      style={{ 
        flex: 1,
        minHeight: 400,
        width: '100%',
        overflow: 'visible',
        shadowOpacity: 0,
      }}
      ref={scrollViewRef}
    >
      {/* Main Registration Card */}
      <View
        style={[containerStyles, {
          width: "80%",
          paddingVertical: "10%"
        }]}
      >
        {/* Registration Form */}
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={[
            styles.loginFormContainer,
            responsiveBreakpoints.isMobile && {
              paddingBottom: 20, // Additional bottom padding for mobile scroll
            }
          ]}
          contentContainerStyle={responsiveBreakpoints.isMobile ? {} : undefined}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === 'web' ? "none" : "on-drag"}
        >
          <Tabs
            defaultValue="sign-in"
            orientation="horizontal"
            flexDirection="column"
            width="100%"
            borderRadius="$4"
            backgroundColor={theme ? 'transparent' : 'rgba(255, 255, 255, 0.05)'}
            value={activeState === 'sign-up' ? 'social' : 'sign-in'}
          >
            {
              flipCard ?
                <></>
                :
                <H3>Please register before proceeding to checkout</H3>
            }

            <Tabs.Content backgroundColor={"transparent"} value="sign-in" padding="$2">
              {/* Enhanced Email/Password Login Form - Fixed at bottom */}
              <Formik
                innerRef={loginForm}
                initialValues={{
                  email: null,
                  password: null,
                }}
                validateOnChange={false}
                validateOnBlur={Platform.OS === 'web' && isTouchDeviceRef.current ? false : true}
                validateOnMount={false}
                onSubmit={async values => {
                  setTimeout(() => { setShowForgotPassword(true); }, 1000);
                  dispatch(loginUser(values, (data: { id: undefined; success: boolean }) => {
                    if (data.success) {
                      navigation.navigate('Dashboard' as never)
                    }
                    if (data?.id === undefined) {
                      console.log('no data stored in id')
                    }
                  }))
                }}
              >{({ handleChange, handleBlur, handleSubmit, values }) => (
                <YStack
                  flex={1}
                  style={styles.loginFormContainer}
                  >
                  {/* Enhanced Email/Password Login Form - Fixed at bottom */}
                  <YStack
                    id="emailPasswordLoginSection"
                    gap={20}
                    paddingBottom={15}
                  // space={responsive.spacing.sm}
                  >
                    {/* Modern Email Section Divider */}

                    {/* Enhanced Email and Password Inputs */}
                    <XStack style={styles.loginInputContainer}>
                      <Input
                          ref={emailInputRef}
                        style={styles.loginInput}
                        placeholder="Email"
                          {...(Platform.OS !== 'web' && { placeholderTextColor: "rgba(100, 116, 139, 0.7)" })}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        onChange={(e) => handleChange('email')(e.nativeEvent.text)}
                        componentName="email"
                          onFocus={handleEmailFocus}
                          onBlur={handleEmailBlur}
                          blurOnSubmit={false}
                      />
                    </XStack>
                    <XStack style={styles.loginInputContainer}>
                      <Input
                          ref={passwordInputRef}
                        style={styles.loginInput}
                        placeholder="Enter your password"
                          {...(Platform.OS !== 'web' && { placeholderTextColor: "rgba(100, 116, 139, 0.7)" })}
                        secureTextEntry={passwordHidden}
                        onChange={(e) => handleChange('password')(e.nativeEvent.text)}
                        componentName="password"
                        autoCapitalize="none"
                          onFocus={handlePasswordFocus}
                          onBlur={handlePasswordBlur}
                          blurOnSubmit={false}
                      />
                      <TouchableOpacity
                        onPress={() => setPasswordHidden(!passwordHidden)}
                        style={styles.loginPasswordToggle}
                      >
                        <FontAwesome6
                          style={{
                            color: passwordHidden ? '#64748b' : '#3B82F6',
                          }}
                          name={passwordHidden ? "eye" : "eye-slash"}
                          size={responsiveBreakpoints.isMobile ? 18 : 20}
                        />
                      </TouchableOpacity>
                    </XStack>
                    {/* Enhanced Submit Button */}
                    <Button
                        backgroundColor="#3B82F6"
                      size={responsiveBreakpoints.isMobile ? "$3" : "$4"}
                      onPress={() => {
                        if (isConnected) {
                          handleSubmit()
                        } else {
                          dispatch({ type: HIDE_ALL_MODALS });
                          dispatch({
                            type: SHOW_AUTH_MODAL, data: {
                              content: "No network connection", type: "error"
                            }
                          })
                        }
                      }}
                      icon={<FontAwesome name="user" color={"white"} size={24} />}>
                      <Text style={styles.loginSubmitButtonText}>
                        Sign In
                      </Text>
                      </Button>
                    {/* {
                        __DEV__ &&
                      <Button buttonType="link" onPress={() => navigation.navigate("Registration")} >
                        Registration page
                      </Button>
                      } */}
                    </YStack>
                    {/* Enhanced Social Login Section - Scrollable */}
                    <YStack
                      id="socialLoginSection"
                      space={responsive.spacing.sm}
                      style={styles.loginSocialSection}
                    >
                      {/* Modern Divider with Enhanced Typography */}
                      <XStack style={styles.loginSocialDividerContainer}>
                        <View style={styles.loginSocialDividerLine} />
                        <Text style={[styles.loginSocialDividerText, { padding: 5, paddingHorizontal: 10, marginHorizontal: 10 }]}>
                          Or Sign Up / Sign In with
                        </Text>
                        <View style={styles.loginSocialDividerLine} />
                      </XStack>

                      {/* Enhanced Social Login Buttons Container */}
                      <ScrollView
                        style={styles.loginSocialScrollView}
                        contentContainerStyle={{
                          paddingHorizontal: responsive.spacing.lg,
                          gap: responsiveBreakpoints.isMobile ? responsive.spacing.sm : responsive.spacing.md,
                        }}
                        showsVerticalScrollIndicator={false}
                      >
                        {codeVerified?.data ? (
                          <View style={{ height: 90 }}>
                            <Card height={90} marginBottom={Platform.select({ android: 20 })}>
                              <Card.Header top={-20}>
                                <H4 style={{ fontSize: 16, fontWeight: "bold", marginBottom: 15 }}>
                                  {codeVerified?.data?.sharedBy?.name ? (
                                    'Thanks to ' + codeVerified.data.sharedBy.name
                                  ) : (
                                    "Promo detected!"
                                  )}
                                </H4>
                                <XStack top={-15}>
                                  <H6 style={{ fontSize: 14, flex: 1, top: 5, lineHeight: 20 }}>
                                    {(() => {
                                      const coupon = codeVerified?.data?.coupon;
                                      const discount = coupon?.percent_off
                                        ? `${coupon.percent_off}% off`
                                        : coupon?.amount_off
                                          ? `$${(coupon.amount_off / 100).toFixed(2)} off`
                                          : '0% off';
                                      const duration = coupon?.duration_in_months
                                        ? ` for ${coupon.duration_in_months} months`
                                        : '';
                                      return `You have received ${discount} your subscription${duration}!`;
                                    })()}
                                  </H6>
                                </XStack>
                              </Card.Header>
                            </Card>
                          </View>
                        ) : null}
                        {codeVerified?.data ? (
                          <LottieView source={require("@assets/animations/celebration.json")} loop={false} autoPlay style={{
                            pointerEvents: "none",
                            top: responsiveBreakpoints.isMobile ? -30 : -50,
                            minWidth: responsiveBreakpoints.isMobile ? 150 : 250,
                            zIndex: 10,
                            minHeight: responsiveBreakpoints.isMobile ? 150 : 250,
                            position: "absolute"
                          }} />
                        ) : null}

                        {/* Enhanced Responsive Social Login Buttons */}
                        <YStack
                          space={responsiveBreakpoints.isMobile ? 12 : 16}
                        >
                          <GoogleButton code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} productId={productId} />
                          <MicrosoftButton code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                          <LinkedInButton productId={productId} code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                          <DiscordButton productId={productId} code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                          <AppleButton productId={productId} code={codeVerified?.data ? (getCodeString(code) || codeVerified.data.code) : undefined} />
                          <TouchableOpacity
                            style={{
                              borderRadius: 8,
                              backgroundColor: '#fff',
                              borderWidth: 1,
                              borderColor: '#e5e7eb',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              paddingVertical: 12,
                              paddingHorizontal: 20,
                              shadowColor: '#000',
                              shadowOpacity: 0.06,
                              shadowOffset: { width: 0, height: 3 },
                              shadowRadius: 6,
                              marginBottom: 4,
                            }}
                            activeOpacity={0.85}
                            onPress={() => setActiveState('sign-up')}
                          >
                            <FontAwesome
                              name="envelope"
                              size={20}
                              color="#EA4335"
                              style={{ marginRight: 12 }}
                            />
                            <SizableText
                              size="$4"
                              color="#222"
                              fontWeight="700"
                            >
                              Sign up with Email
                            </SizableText>
                          </TouchableOpacity>
                        </YStack>
                        {__DEV__ ? (
                          <XStack
                            flex={1}
                            space={responsiveBreakpoints.isMobile ? 12 : 20}
                            flexWrap="wrap"
                            rowGap={20}
                            justifyContent="center"
                            style={{
                              backgroundColor: 'rgba(239, 68, 68, 0.05)',
                              padding: responsive.spacing.md,
                              borderRadius: 12,
                              borderWidth: 1,
                              borderColor: 'rgba(239, 68, 68, 0.2)',
                              marginTop: responsive.spacing.sm,
                              ...(Platform.OS === 'web' && {
                                backdropFilter: 'blur(4px)',
                              })
                            }}
                          >
                            <Button
                              size={responsiveBreakpoints.isMobile ? "$2" : "$3"}
                              backgroundColor="#dc2626"
                              onPress={async () => {
                                try {
                                  // Create mock user payload for offline bypass
                                  const mockResponse = {
                                    data: {
                                      data: {
                                        userData: {
                                          id: "offline-bypass-user-123",
                                          _id: "offline-bypass-user-123",
                                          email: "offline@smartsaas.co.uk",
                                          name: "Offline",
                                          surname: "User",
                                          defaultCompany: null,
                                          favourites: [],
                                          security: { mfa: false },
                                          city: null,
                                          country: "",
                                          recents: [],
                                          avatar: null,
                                          posts: [],
                                          affiliateCode: null,
                                          achievements: {
                                            achievement_points: 0,
                                            achievedList: []
                                          },
                                          billing: {},
                                          lastLogin: null,
                                          authentications: {},
                                          tutorials: {
                                            tutorialModal: false
                                          },
                                          companies: []
                                        },
                                        notification_info: [],
                                        company: null
                                      }
                                    }
                                  };

                                  // Process the mock data the same way as real API response
                                  const amendedData = await handleUserData(mockResponse);

                                  // Dispatch SUCCESS to update Redux state
                                  dispatch({ type: SUCCESS, data: amendedData });

                                  // Navigate to Dashboard (use replace to avoid back navigation to login)
                                  (navigation as any).replace('Dashboard', {
                                    screen: 'Welcome',
                                    params: { session: amendedData }
                                  });
                                } catch (error) {
                                  console.error('Bypass Offline error:', error);
                                }
                              }}
                            >Bypass Offline
                            </Button>
                            <Button
                              size={responsiveBreakpoints.isMobile ? "$2" : "$3"}
                              onPress={async () => {
                                const res = await apis.handleOAuth({
                                  "id": "123",
                                  "email": "fareed@smartsaas.co.uk",
                                  "name": "John",
                                  "surname": "Doe",
                                  "oauthToken": "token123",
                                  "type": "basic",
                                  "notificationToken": "notif123",
                                  "device": {
                                    "brand": "Apple",
                                    "model": "iPhone 13",
                                    "os": "iOS",
                                    "osVersion": "17.3"
                                  }
                                })
                                const amendedData = await handleUserData(res);
                                dispatch({ type: SUCCESS, data: amendedData });
                                navigation.navigate('Dashboard' as never);
                              }}
                              backgroundColor="#059669"
                            >
                              Bypass Online
                            </Button>
                            <Button onPress={async () => {
                              try {
                                dispatch({ type: SHOW_LOADING_MODAL })
                                const randomGoogleUser = createRandomGoogleUser(notificationToken || "");
                                const res = await apis.handleOAuth(randomGoogleUser);
                                await handleOAuthResponse(res, dispatch, navigation, notificationToken || "", 'Google');
                              } catch (error) {
                                console.error('Google dev login error:', error);
                                dispatch({ type: HIDE_LOADING_MODAL })
                              }
                            }}>
                            Dev Login with Google
                            </Button>
                          </XStack>
                        ) : null}
                      </ScrollView>
                    </YStack>
                  </YStack>
                )}
              </Formik>
            </Tabs.Content>

            {/* Social Login Tab */}
            <Tabs.Content value="social" padding="$2">
              <YStack space="$3" alignItems="center">
                {/* Code Detection Visual Indicator for Sign-up */}
                {codeVerified?.data ? (
                  <View style={{ height: 90, width: '100%' }}>
                    <Card height={90} marginBottom={Platform.select({ android: 20 })}>
                      <Card.Header top={-20}>
                        <H4 style={{ fontSize: 16, fontWeight: "bold", marginBottom: 15 }}>
                          {codeVerified?.data?.sharedBy?.name ? (
                            'Thanks to ' + codeVerified.data.sharedBy.name
                          ) : (
                            "Promo detected!"
                          )}
                        </H4>
                        <XStack top={-15}>
                          <H6 style={{ fontSize: 14, flex: 1, top: 5, lineHeight: 20 }}>
                            {(() => {
                              const coupon = codeVerified?.data?.coupon;
                              const discount = coupon?.percent_off
                                ? `${coupon.percent_off}% off`
                                : coupon?.amount_off
                                  ? `$${(coupon.amount_off / 100).toFixed(2)} off`
                                  : '0% off';
                              const duration = coupon?.duration_in_months
                                ? ` for ${coupon.duration_in_months} months`
                                : '';
                              return `You have received ${discount} your subscription${duration}!`;
                            })()}
                          </H6>
                        </XStack>
                      </Card.Header>
                    </Card>
                  </View>
                ) : null}
                {codeVerified?.data ? (
                  <LottieView source={require("@assets/animations/celebration.json")} loop={false} autoPlay style={{
                    pointerEvents: "none",
                    top: responsiveBreakpoints.isMobile ? -30 : -50,
                    minWidth: responsiveBreakpoints.isMobile ? 150 : 250,
                    zIndex: 10,
                    minHeight: responsiveBreakpoints.isMobile ? 150 : 250,
                    position: "absolute"
                  }} />
                ) : null}
                {formData ? (
                  <View
                    style={{ paddingHorizontal: "5%", transform: [{ scale: 0.8 }] }}
                  >
                    <H3>Thank you for registering!</H3>
                    <Paragraph>
                      A confirmation email has been sent to {formData?.email}. Please check your inbox and follow the instructions to verify your email address and complete your registration.
                    </Paragraph>
                  </View>
                ) : (
                  <GenerateSchemaForm
                    ref={formRef}
                      hideButton
                      buttonLabel='Create Account'
                      schema={registrationSchema}
                      submitFunction={handleRegistrationSubmit}
                  />
                )}
                <Separator
                  backgroundColor={theme ? '$borderColor' : 'rgba(255, 255, 255, 0.2)'}
                  marginVertical="$4"
                  width="80%"
                />
                <SizableText
                  size="$2"
                  textAlign="center"
                  color={theme ? '$grey8' : '$grey6'}
                  maxWidth="80%"
                >
                  By continuing with any option, you agree to our Terms of Service and Privacy Policy
                </SizableText>
              </YStack>
            </Tabs.Content>
          </Tabs>
        </ScrollView>
        {/* Footer Section */}
        <YStack space="$3" paddingTop="$4">
          <XStack alignSelf='stretch' gap="$4" justifyContent="space-between" paddingHorizontal="$4">
            {activeState === 'sign-up' && (
              <TouchableOpacity
                style={{
                  flex: 1,
                  backgroundColor: 'rgba(100, 116, 139, 0.4)', // subtle greyish translucent background
                  flexDirection: 'row',
                  padding: 12,
                  alignSelf: 'flex-start',
                  borderRadius: 8,
                  minWidth: 130,
                  gap: 10,
                  alignItems: 'center',
                }}
                onPress={() => setActiveState('sign-in')}>
                  <FontAwesome6
                    name="arrow-left"
                    size={16}
                    color="white"
                  />
                <SizableText
                  size="$3"
                  color="white"
                  fontWeight="600"
                >
                  Back to Sign In
                </SizableText>
              </TouchableOpacity>
            )}
            {activeState === 'sign-up' && !formData ? (
              <TouchableOpacity
                style={{
                  flex:1,
                  alignSelf: 'flex-end',
                  backgroundColor: '#3B82F6',
                  padding: 12,
                  borderRadius: 8,
                  minWidth: 100,
                  alignItems: 'center'
                }}
                onPress={() => {
                  if (formRef.current) {
                    try {
                      // First validate the form
                      formRef.current.validateForm().then((errors) => {
                        if (Object.keys(errors).length === 0) {
                          // No validation errors, proceed with submission
                          formRef.current.submitForm();
                        } else {
                          // Validation errors exist
                          // Set touched fields to show errors
                          formRef.current.setTouched(
                            Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
                          );
                        }
                      });
                    } catch (error) {
                      // Try handleSubmit as fallback
                      if (formRef.current.handleSubmit) {
                        formRef.current.handleSubmit();
                      }
                    }
                  } else {
                    console.log("formRef.current is null or undefined");
                  }
                }}>
                <XStack space="$2" alignItems="center">
                    <SizableText
                      size="$3"
                      color="white"
                      fontWeight="600"
                    >
                      Register
                    </SizableText>
                    <FontAwesome6
                      name="arrow-right"
                      size={16}
                      color="white"
                    />
                  </XStack>
                </TouchableOpacity>
            ) : null}
          </XStack>
        </YStack>
      </View>
    </View>
  );
};

export default memo(Registration);
